import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rezolve-green focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-rezolve-green text-white shadow-brand hover:bg-rezolve-green-dark hover:shadow-brand-lg hover:-translate-y-0.5",
        destructive: "bg-gradient-to-r from-rose-500 to-red-500 text-white shadow-sm hover:from-rose-600 hover:to-red-600 hover:-translate-y-0.5",
        outline: "border-2 border-rezolve-green/30 bg-background shadow-sm hover:bg-rezolve-green/10 hover:border-rezolve-green/50 hover:text-rezolve-green-dark",
        secondary: "bg-secondary text-rezolve-navy shadow-sm hover:bg-rezolve-green/10 hover:text-rezolve-green-dark",
        ghost: "hover:bg-rezolve-green/10 hover:text-rezolve-green-dark",
        link: "text-rezolve-green underline-offset-4 hover:underline hover:text-rezolve-green-dark",
        success: "bg-gradient-success text-white shadow-sm hover:opacity-90 hover:-translate-y-0.5",
        brand: "bg-gradient-brand text-white shadow-brand hover:shadow-brand-lg hover:-translate-y-0.5",
        navy: "bg-rezolve-navy text-white shadow-navy hover:bg-rezolve-navy-light hover:-translate-y-0.5",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
        "icon-sm": "h-7 w-7",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, ...props }, ref) => {
    return (
      <button
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
